import { FC, useState } from 'react';
import cn from 'clsx';
import { makeStyles } from '@material-ui/styles';
import { Modal } from '@material-ui/core';

import { usePostMessageListener, ListenerActions } from '@bizzabo/dev-platform-client-sdk';
import { UserData } from '@bizzabo/entities';
import { CloseFill, IconButton } from '@bizzabo/design-system';

import AppFrame from '../AppFrame';
import { ModalSize } from '../../types/appExtension';
import { extractOriginFromUrl } from '../../utils/helpers';

type Props = {
    url: string;
    appExtensionUrl: string;
    appExtensionClientId: string;
    closeModal: () => void;
    size: ModalSize;
    accountId: number;
    eventId: number;
    user?: UserData;
};

const AppModal: FC<Props> = ({
    url,
    appExtensionUrl,
    appExtensionClientId,
    closeModal,
    size,
    accountId,
    eventId,
    user,
}) => {
    const classes = useStyles();
    const [isModelOpen, setIsModalOpen] = useState<boolean>(true);

    const handleCloseModal = () => {
        setIsModalOpen(false);
        closeModal();
    };

    const appOrigin = extractOriginFromUrl(appExtensionUrl);

    usePostMessageListener(
        [appOrigin],
        appExtensionClientId,
        ListenerActions.APP_EXT_CLOSE_MODAL,
        handleCloseModal
    );

    return (
        <Modal open={isModelOpen} onClose={closeModal}>
            <div className={cn(classes.modal, classes[size])}>
                <div className={classes.closeHeader}>
                    <div className={classes.closeSection}>
                        <IconButton
                            onClick={closeModal}
                            icon={CloseFill}
                            className={classes.closeBtn}
                        />
                    </div>
                </div>
                <AppFrame
                    appExtensionUrl={url}
                    appExtensionClientId={appExtensionClientId}
                    accountId={accountId}
                    eventId={eventId}
                    user={user}
                />
            </div>
        </Modal>
    );
};

export const useStyles = makeStyles(
    theme => ({
        modal: {
            outline: 'none', // prevents :focus-visible  border
            display: 'flex',
            flexDirection: 'column',
            position: 'absolute',
            top: '50%',
            left: '50%',
            width: '90%',
            boxShadow: '0px 4px 24px rgba(0, 0, 0, 0.1)',
            borderRadius: 33,
            backgroundColor: theme.palette.greys[100],
            transform: `translate(-50%, -50%)`,
            [theme.breakpoints.up('sm')]: {
                maxWidth: 1024,
                height: '80vh',
            },
        },

        closeHeader: {
            width: '100%',
            minHeight: 72,
            display: 'flex',
            justifyContent: 'flex-end',
        },

        closeSection: {
            ...theme.typography.t80,
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            borderBottom: 'none',
            paddingRight: 16,
            backgroundColor: theme.palette.greys[100],
            borderTopRightRadius: 33,
            '& svg': {
                width: 25,
                height: 25,
            },
        },

        closeBtn: {
            backgroundColor: theme.palette.greys[100],
        },

        sm: {
            [theme.breakpoints.up('sm')]: {
                width: 500,
                height: 400,
            },
        },

        md: {
            [theme.breakpoints.up('sm')]: {
                width: 700,
                height: 560,
            },
        },

        lg: {
            [theme.breakpoints.up('sm')]: {
                width: 900,
                height: 650,
            },
        },
        xl: {
            [theme.breakpoints.up('sm')]: {
                width: 1024,
                height: 740,
            },
        },
        max: {
            [theme.breakpoints.up('sm')]: {
                width: `calc(100vw - 100px)`,
                height: `calc(100vh - 50px)`,
                maxWidth: `calc(100vw - 100px)`,
            },
        },
    }),
    { classNamePrefix: 'app-modal' }
);

export default AppModal;
